<template>
  <div class="bm-floating-nav top-4 left-4 right-4 md:left-6 md:right-6">
    <div class="bm-glass bm-topbar flex items-center justify-between px-3 py-2 md:px-4 md:py-2">
      <router-link to="/" class="flex items-center gap-2 no-underline">
        <span class="bm-brand-mark">
          <img src="/favicon.svg" alt="BarterMent" class="w-6 h-6" />
        </span>
        <span class="bm-display-font text-lg font-semibold bm-hero-title">
          BarterMent
        </span>
      </router-link>

      <div class="bm-topbar-actions flex items-center gap-1 md:gap-2">
        <v-btn icon variant="text" size="small" class="bm-pill-glow" @click="toggleTheme">
          <v-icon>{{ uiStore.theme === 'dark' ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
        </v-btn>

        <template v-if="authStore.isAuthenticated">
          <v-btn variant="tonal" size="small" class="hidden sm:inline-flex" to="/browse">
            Browse
          </v-btn>
          <v-btn icon variant="text" size="small" class="bm-pill-glow" to="/notifications">
            <v-badge :content="notificationStore.unreadCount" :model-value="notificationStore.unreadCount > 0" color="error">
              <v-icon>mdi-bell-outline</v-icon>
            </v-badge>
          </v-btn>

          <v-menu>
            <template #activator="{ props }">
              <v-avatar v-bind="props" size="38" class="cursor-pointer ml-1 bm-avatar-ring">
                <v-img v-if="authStore.user?.avatarUrl" :src="authStore.user.avatarUrl" />
                <v-icon v-else>mdi-account-circle</v-icon>
              </v-avatar>
            </template>
            <v-list class="bm-glass" density="comfortable">
              <v-list-item :title="authStore.user?.publicUsername" :subtitle="authStore.user?.email" />
              <v-divider />
              <v-list-item to="/profile" prepend-icon="mdi-account-cog">Profile</v-list-item>
              <v-list-item to="/billing" prepend-icon="mdi-credit-card-outline">Billing</v-list-item>
              <v-list-item v-if="authStore.isAdmin" to="/admin" prepend-icon="mdi-shield-crown-outline">Admin</v-list-item>
              <v-divider />
              <v-list-item prepend-icon="mdi-logout" @click="logout">Log out</v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-else>
          <v-btn variant="text" size="small" class="bm-guest-btn" to="/login">Log in</v-btn>
          <v-btn color="primary" size="small" class="bm-guest-btn" to="/register">Sign up</v-btn>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useAuthStore } from '../../store/auth';
import { useUiStore } from '../../store/ui';
import { useNotificationStore } from '../../store/notifications';

const authStore = useAuthStore();
const uiStore = useUiStore();
const notificationStore = useNotificationStore();
const router = useRouter();

function logout() {
  authStore.logout();
  router.push('/');
}

async function toggleTheme() {
  const theme = uiStore.theme === 'light' ? 'dark' : 'light';
  uiStore.setTheme(theme);
  if (!authStore.isAuthenticated) return;
  try {
    await authStore.updateProfile({ theme });
  } catch {
    // Local preference remains available if the profile write is temporarily unavailable.
  }
}
</script>

<style scoped>
.bm-topbar {
  min-height: 50px;
  border-radius: 999px;
  background: rgba(255, 253, 246, 0.68);
}

.theme--dark .bm-topbar {
  background: rgba(23, 39, 33, 0.74);
}

.bm-brand-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--bm-primary), var(--bm-secondary));
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.3), 0 10px 24px rgba(42, 59, 41, 0.16);
}

.bm-avatar-ring {
  border: 1px solid rgba(255,255,255,0.55);
  box-shadow: 0 10px 24px rgba(42, 59, 41, 0.16);
}

.bm-topbar-actions {
  min-width: 0;
}

.bm-guest-btn {
  min-width: 0;
}

@media (max-width: 520px) {
  .bm-floating-nav {
    left: 10px !important;
    right: 10px !important;
  }

  .bm-topbar {
    min-height: 48px;
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .bm-brand-mark {
    width: 32px;
    height: 32px;
  }

  .bm-hero-title {
    font-size: 1rem !important;
  }

  .bm-guest-btn {
    padding-inline: 10px !important;
    font-size: 0.74rem;
  }
}
</style>
